import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { businessService } from '@/services/business.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Save, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import 'react-international-phone/style.css';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { BUSINESS_SETTINGS_PERMISSION, LTI_INTEGRATION_PERMISSION } from '@/constants/constant-permissions';

import LtiIntegrationSetting from '@/components/lti/LtiIntegrationSetting';

// Import section components from forms
import {
  CompanyInformationSection,
  BrandAssetsSection,
  GeneralSettingsSection,
  CertificationSettingsSection,
  OrganizationalNotificationsSection,
  SocialMediaIntegrationSection,
} from '@/components/forms/business-settings';
import { InputField } from '@/components/common/form-fields';
import { extractErrorMessage } from '@/utils/error-helper';

export default function BusinessSettings() {
  usePermissionRouteAccess([
    BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_READ,
    BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_WRITE,
  ]);
  const canWrite = useHasPermission([BUSINESS_SETTINGS_PERMISSION.ADMIN_BUSINESS_SETTINGS_WRITE]);
  const canWriteLti = useHasPermission([LTI_INTEGRATION_PERMISSION.ADMIN_LTI_INTEGRATION_WRITE]);
  const queryClient = useQueryClient();
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  const [showSmallLogoPreview, setShowSmallLogoPreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [phone, setPhone] = useState('');

  // Fetch business settings
  const { data, isLoading } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
  });

  // The API omits hide_classic_login for tenants that never set it; default it to
  // false so the checkbox renders unticked and classic login stays visible.
  const formValues = useMemo(
    () => (data?.data ? { ...data.data, hide_classic_login: data.data.hide_classic_login ?? false } : undefined),
    [data?.data]
  );

  // Initialize form with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm({
    // No resolver: the shared Input applies its clean-text rule on top of each field's rules
    values: formValues,
    // Validate while typing so errors (e.g. clean-text) show under the field, not only after Save
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // Cross-field rules: when a feature is enabled, at least one recipient/approver must be ticked
  register('notify_hrbp', {
    validate: (_value, form) =>
      !form.enable_organizational_email_notifications ||
      form.notify_bu_head || form.notify_reporting_manager || form.notify_hrbp ||
      'Please select at least one notification recipient',
  });
  register('self_ticket_approvers.all_admins', {
    validate: (_value, form) => {
      const approvers = form.self_ticket_approvers || {};
      return !form.enable_self_ticket_assignment ||
        approvers.bu_head || approvers.reporting_manager || approvers.hrbp || approvers.all_admins ||
        'Please select at least one approver';
    },
  });

  // Update mutation - now uses saveBusinessSettings (POST) instead of updateBusinessSettings (PUT)
  const updateMutation = useMutation({
    mutationFn: (data: any) => businessService.saveBusinessSettings(data),
    onSuccess: () => {
      toast.success('Business settings saved successfully!');
      queryClient.invalidateQueries({ queryKey: ['businessSettings'] });
    },
    onError: (error: Error) => {
      toast.error(extractErrorMessage(error, 'Failed to save settings.'));
    },
  });

  // Handle logo removal
  const handleRemoveLogo = (type: 'large' | 'small') => {
    if (type === 'large') {
      setValue('large_logo_url', '', { shouldDirty: true });
    } else {
      setValue('small_logo_url', '', { shouldDirty: true });
    }
    setPreviewImageUrl(null);
    toast.success(`${type === 'large' ? 'Expanded menu' : 'Collapsed menu'} logo removed`);
  };

  // Handle logo preview
  const handleViewLogo = (type: 'large' | 'small') => {
    const formValues = watch();
    const imageUrl = type === 'large'
      ? formValues.large_logo_url || data?.data?.large_logo_url
      : formValues.small_logo_url || data?.data?.small_logo_url;

    setPreviewImageUrl(imageUrl);
    if (type === 'large') {
      setShowLogoPreview(true);
    } else {
      setShowSmallLogoPreview(true);
    }
  };

  // Form submission handler
  const onSubmit = (formData: any) => {
    const submissionData = {
      ...formData,
      company_name: formData.company_name?.trim(),
      contact_phone_number: phone || formData.contact_phone_number || '',
      large_logo_url: formData.large_logo_url || '',
      small_logo_url: formData.small_logo_url || '',
    };
    updateMutation.mutate(submissionData);
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 rounded-xl p-6 text-white">
          <SkeletonLoader type="title" className="bg-white/20 mb-2" />
          <SkeletonLoader type="text" width="60%" className="bg-white/20" />
        </div>

        {/* Company Information Skeleton */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <SkeletonLoader type="title" className="mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <SkeletonLoader type="text" width="30%" className="mb-2" />
                <SkeletonLoader type="custom" className="h-12 w-full rounded-lg" />
              </div>
            ))}
          </div>
        </div>

        {/* Branding Skeleton */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <SkeletonLoader type="title" className="mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i}>
                <SkeletonLoader type="text" width="40%" className="mb-2" />
                <div className="flex items-center gap-4">
                  <SkeletonLoader type="custom" className="w-24 h-24 rounded-lg" />
                  <SkeletonLoader type="button" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* General Settings Skeleton */}
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <SkeletonLoader type="title" className="mb-6" />
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <SkeletonLoader type="custom" className="w-5 h-5 rounded" />
              <SkeletonLoader type="text" width="50%" />
            </div>
            <div>
              <SkeletonLoader type="text" width="40%" className="mb-2" />
              <SkeletonLoader type="custom" className="h-12 w-full rounded-lg" />
            </div>
          </div>
        </div>

        {/* Save Button Skeleton */}
        <div className="flex justify-end">
          <SkeletonLoader type="button" width="120px" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Business Settings</h1>
        <p className="text-brand-blue-100">Manage your company's information and branding.</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <fieldset disabled={!canWrite} className={`space-y-6 ${!canWrite ? 'opacity-60' : ''}`}>
        {/* Company Information Section */}
        <CompanyInformationSection
          control={control}
          errors={errors}
          phone={phone}
          setPhone={setPhone}
        />

        {/* Brand Assets Section */}
        <BrandAssetsSection
          watch={watch}
          setValue={setValue}
          onRemoveLogo={handleRemoveLogo}
          onViewLogo={handleViewLogo}
          data={data}
        />

        {/* General Settings Section */}
        <GeneralSettingsSection
          register={register}
          watch={watch}
          setValue={setValue}
        />

        {/* Certification Settings Section */}
        <CertificationSettingsSection
          control={control}
        />

        </fieldset>

        {/* LTI Integration — outside the fieldset above on purpose. A disabled
            fieldset also disables its children, which would ignore canWriteLti. */}
        <LtiIntegrationSetting canWrite={canWriteLti} />

        <fieldset disabled={!canWrite} className={`space-y-6 ${!canWrite ? 'opacity-60' : ''}`}>

        {/* Social Media Integration Section */}
        <SocialMediaIntegrationSection
          control={control}
        />

        {/* Course Completion Settings */}
        <div className="card">
          <div className="flex items-center gap-2 mb-6">
            <GraduationCap className="w-5 h-5 text-brand-blue-600" />
            <h2 className="text-lg font-semibold">Course Completion Settings</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Course completion is calculated based on time spent vs estimated time for each content item.
          </p>
          <div className="max-w-sm">
            <InputField
              label="Course Completion Threshold (%)"
              description="Minimum percentage of course progress required to consider a course as completed."
              fieldType="number"
              min={1}
              max={100}
              placeholder="e.g. 80"
              error={errors.course_completion_threshold?.message as string}
              {...register('course_completion_threshold', {
                // Empty means "not set"; otherwise send a number, as the old schema did
                setValueAs: (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
                validate: (value) =>
                  value === undefined || (value >= 1 && value <= 100) ||
                  (value < 1 ? 'Must be greater than 0' : 'Must be less than or equal to 100'),
              })}
            />
          </div>
        </div>

        {/* Organizational Notifications Section */}
        <OrganizationalNotificationsSection
          register={register}
          watch={watch}
          control={control}
          errors={errors}
        />
        </fieldset>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className={`btn btn-primary inline-flex items-center${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canWrite || updateMutation.isPending}
            title={!canWrite ? 'You do not have permission' : undefined}
          >
            <Save className="w-4 h-4 mr-2" />
            {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Logo Preview Modal */}
      {(showLogoPreview || showSmallLogoPreview) && previewImageUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {showLogoPreview ? 'Expanded Menu Logo' : 'Collapsed Menu Logo'} Preview
              </h3>
              <button
                onClick={() => {
                  setShowLogoPreview(false);
                  setShowSmallLogoPreview(false);
                  setPreviewImageUrl(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex justify-center bg-gray-50 rounded-xl p-8">
              <img
                src={previewImageUrl}
                alt="Logo Preview"
                className="max-w-full max-h-96 object-contain rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
